import React, { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { processAPIError, filterUncommonErrors, translate } from 'utils';
import { renderErrors } from 'app/Components/common/Error';
import { shouldShowMoreShow, ShowMore } from 'app/Components/common/ShowMore';
import { TError } from 'models';
import { DrawerBodyStyled, DrawerCloseIcon, DrawerDataForm, DrawerHeaderRow, DrawerHeaderRowTitle } from 'app/Components/common/Drawer/styles';
import { TBroker } from 'app/Pages/common';
import { Input } from '@insly/qmt-reactjs-ui-lib';
import { renderControls, scrollElementToBottom } from 'app/Components/common/Drawer';
import { ContactsItem, ContactPersonRow } from 'styles/common';

const TITLE = translate({ key: 'master.broker.add' });

const USER_TEMPLATE = {
  id: '',
  name: '',
  tag: ''
};

interface IProps {
  handleDrawerClose: () => void,
  handlePostUser: (userForm: TBroker, onError: (error: AxiosError) => void) => void,
}

//@TODO: Update Drawer Lib component and make children not required and remove unnecessary <div />
const DrawerAddBroker = ({ handleDrawerClose, handlePostUser }: IProps) => (
  <>
    {renderHeaderRow(TITLE, handleDrawerClose)}
    <DrawerBody
      handlePostUser={handlePostUser}
    />
  </>
);

const DrawerBody = ({
  handlePostUser,
}: {
  handlePostUser: (userForm: TBroker, onError: (error: AxiosError) => void, onSuccess: () => void) => void,
}) => {
  const bodyEl = useRef<HTMLDivElement>(null);
  const [showMore, updateShowMore] = useState(false);

  useEffect(() => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBodyScroll = () => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
  };

  return (
    <DrawerBodyStyled ref={bodyEl} onScroll={onBodyScroll} showMore={true}>
      <UserForm
        handlePostUser={handlePostUser}
      />
      <ShowMore
        show={showMore}
        onClick={() => scrollElementToBottom(bodyEl.current)}
      />
    </DrawerBodyStyled>
  );
};

const renderHeaderRow = (title: string, handleDrawerClose: () => void) => (
  <DrawerHeaderRow>
    <DrawerHeaderRowTitle>{title}</DrawerHeaderRowTitle>
    <DrawerCloseIcon icon="close" onClick={handleDrawerClose} />
  </DrawerHeaderRow>
);

const UserForm = ({
  handlePostUser,
}: {
  handlePostUser: (userForm: TBroker, onError: (error: AxiosError) => void, onSuccess: () => void) => void,
}) => {

  const [form, updateForm] = useState<TBroker>({
    ...USER_TEMPLATE,
  });

  const [errors, updateErrors] = useState<TError[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const [isSubmitting, updateIsSubmitting] = useState(false);

  const handleChange = (key?: string, value?: string) => {
    const touchedForm = { ...form };
    // @ts-ignore
    touchedForm[key] = value;

    updateErrors([]);
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const handleSaveData = () => {
    updateIsSubmitting(true);

    handlePostUser(form, (error) => {
      updateErrors(() => processAPIError(error, true) as TError[]);
      updateIsSubmitting(false);
    }, () => {
      updateIsTouched(false);
      updateIsSubmitting(false);
    });
    updateIsTouched(false);
  };

  return (
    <DrawerDataForm>
      {isSubmitting ? <LoadingOverlay /> : null}
      {renderUsersForm(form, handleChange)}
      {errors?.length ? renderErrors(filterUncommonErrors(errors)) : null}
      {(isTouched) ? renderControls('list', isTouched, handleSaveData,) : null}
    </DrawerDataForm>
  );

};

const renderUsersForm = (form: TBroker, handleChange: (key?: string, value?: string) => void) => (
  <ContactsItem>
    <ContactPersonRow gridTemplate="1fr">
      <Input
        label={translate({ key: 'master.common.name' })}
        name="name"
        value={form.name}
        handleChange={handleChange}
      />
    </ContactPersonRow>
    <ContactPersonRow gridTemplate="1fr">
      <Input
        label={translate({ key: 'master.common.tag' })}
        name="tag"
        value={form.tag}
        handleChange={handleChange}
      />
    </ContactPersonRow>
  </ContactsItem>
);

export default DrawerAddBroker;
